import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AvatarBadge } from "@/components/Avatars";
import { PenLine, Menu, X, LogOut, ShieldCheck, GraduationCap, Home, User as UserIcon, Sparkles, Handshake, Monitor, Smartphone } from "lucide-react";
import { ZOOD_LOGO } from "@/lib/brand";
import { useViewMode } from "@/lib/viewMode";

const NAV_BY_ROLE = {
  student: [
    { to: "/", label: "Home", end: true },
    { to: "/writing", label: "Cursive" },
    { to: "/assignments", label: "Assignments" },
    { to: "/achievements", label: "Achievements" },
    { to: "/certificates", label: "Certificates" },
  ],
  teacher: [
    { to: "/", label: "My Students", end: true },
    { to: "/library", label: "Library" },
    { to: "/lessons", label: "Lessons" },
    { to: "/writing", label: "Cursive" },
    { to: "/worksheets", label: "Worksheets" },
    { to: "/subscription", label: "My Plan" },
    { to: "/pricing", label: "Plans" },
    { to: "/help", label: "Help" },
  ],
  parent: [
    { to: "/", label: "My Students", end: true },
    { to: "/library", label: "Library" },
    { to: "/lessons", label: "Lessons" },
    { to: "/writing", label: "Cursive" },
    { to: "/worksheets", label: "Worksheets" },
    { to: "/subscription", label: "My Plan" },
    { to: "/pricing", label: "Plans" },
    { to: "/help", label: "Help" },
  ],
  reseller: [
    { to: "/", label: "Dashboard", end: true },
    { to: "/library", label: "Library" },
    { to: "/pricing", label: "Plans" },
    { to: "/subscription", label: "My Plan" },
    { to: "/help", label: "Help" },
  ],
  school_admin: [
    { to: "/", label: "School", end: true },
    { to: "/library", label: "Library" },
    { to: "/pricing", label: "Plans" },
    { to: "/subscription", label: "My Plan" },
    { to: "/help", label: "Help" },
  ],
  district_admin: [
    { to: "/", label: "District", end: true },
    { to: "/library", label: "Library" },
    { to: "/pricing", label: "Plans" },
    { to: "/subscription", label: "My Plan" },
    { to: "/help", label: "Help" },
  ],
  admin: [
    { to: "/", label: "Users", end: true },
    { to: "/lessons", label: "Lessons" },
    { to: "/plans", label: "Plans" },
    { to: "/curriculum", label: "Cursive" },
    { to: "/stats", label: "Stats" },
    { to: "/settings", label: "Settings" },
    { to: "/help", label: "Help" },
  ],
};

const ROLE_ICON = { admin: ShieldCheck, teacher: GraduationCap, parent: Home, student: UserIcon, reseller: Handshake };

export function Navbar() {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const { mode, toggle: toggleView, isMobileDevice } = useViewMode();

  useEffect(() => {
    if (user) api.brandMe().then((d) => setBrand(d.brand)).catch(() => {});
  }, [user]);

  const links = NAV_BY_ROLE[role] || [];
  const RoleIcon = ROLE_ICON[role] || UserIcon;
  const accent = brand?.accent_color || "#0D9488";

  const linkClass = ({ isActive }) =>
    `px-4 py-2 rounded-full font-semibold text-sm transition-all ${
      isActive ? "text-white shadow-sm" : "text-[#1F2937] hover:bg-[#F2ECE1]"
    }`;
  const activeStyle = ({ isActive }) => (isActive ? { background: accent } : {});

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-40 border-b border-[#E5DECF] bg-[#FAF7F2]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <NavLink to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
          {brand ? (
            <>
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl text-white" style={{ background: accent }}>
                {brand?.logo_url ? <img src={brand.logo_url} alt="" className="h-full w-full object-cover" /> : <Sparkles size={20} strokeWidth={2.4} />}
              </div>
              <div className="font-heading text-base font-extrabold text-[#1F2937]" data-testid="brand-name">{brand.name}</div>
            </>
          ) : (
            <img src={ZOOD_LOGO} alt="ZoodLeap" className="h-9 w-auto sm:h-10" data-testid="brand-name" />
          )}
        </NavLink>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} style={activeStyle} data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user && (
            <div className="hidden items-center gap-2 sm:flex">
              {role === "student" ? (
                <AvatarBadge avatarKey={user.avatar} size={38} />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><RoleIcon size={18} /></div>
              )}
              <div className="leading-tight">
                <div className="font-heading font-bold text-sm text-[#1F2937]">{user.name}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">{role}</div>
              </div>
            </div>
          )}
          <button onClick={doLogout} data-testid="logout-button" className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-[#E05A47] transition-colors hover:bg-[#E05A47]/10 sm:inline-flex">
            <LogOut size={16} /> Log Out
          </button>
          <button onClick={toggleView} data-testid="view-mode-toggle" title={mode === "desktop" ? "Switch to mobile view" : "Switch to desktop view"}
            className="hidden items-center gap-1.5 rounded-full border border-[#E5DECF] px-3 py-2 text-xs font-bold text-[#1F2937] hover:border-[#0D9488] sm:inline-flex">
            {mode === "desktop" ? <><Smartphone size={14} /> Mobile</> : <><Monitor size={14} /> Desktop</>}
          </button>
          <button onClick={() => setOpen((o) => !o)} className="rounded-xl p-2 text-[#1F2937] lg:hidden" data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="flex flex-col gap-1 border-t border-[#E5DECF] px-4 py-3 lg:hidden">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className={linkClass}>{l.label}</NavLink>
          ))}
          <button onClick={() => { toggleView(); setOpen(false); }} data-testid="mobile-view-mode-toggle"
            className="mt-1 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-[#0D9488] hover:bg-[#F2ECE1]">
            {mode === "desktop" ? <><Smartphone size={16} /> Switch to mobile view</> : <><Monitor size={16} /> Switch to desktop view</>}
          </button>
          <button onClick={doLogout} className="mt-1 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-[#E05A47] hover:bg-[#F2ECE1]" data-testid="mobile-logout-button">
            <LogOut size={16} /> Log Out
          </button>
        </nav>
      )}
    </header>
  );
}
